import { bool, func } from 'prop-types';
import styled from '@emotion/styled';
import { BaseRow } from 'shared/containers';
import { RadioCheck, Percentage, Currency, Info, looptijdExplanation } from 'shared/components';
import useMedia from 'shared/hooks/useMedia';
import { shapeResult } from '../config';
import { RiskReductionSvg } from 'images/shared';

const RowContainer = styled(BaseRow)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 72px 24px 24px;
    
    border-color: ${({ theme, active }) => active ? theme.cardColor : null};
    box-shadow: ${({ active }) => active ? '0 0 16px rgba(0, 0, 0, 0.16)' : null};
    
    &:hover {
        cursor: ${({ active }) => !active && 'pointer' };
    }
    
    .provider-label {
      display: none;
    }
    
    @media ${({ theme }) => theme.mobile} {
      padding: 16px;
      align-items: flex-start;
      flex-wrap: wrap;    
        
      .provider-label {
        display: block;
        flex-basis: 100%;
        margin: 16px 0 24px;
        font-size: 14px;
      }
        
    }
`;

const ProductLabel = styled.div`
    flex-basis: 200px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-align: left;
    
    .provider-logo {
      display: block;
      width: auto;
      height: 24px;
      object-fit: contain;
      object-position: left;
      margin-bottom: 4px;
     @supports (object-fit: contain) {
       width: 54px;
     }
    }
    
    .reduction-icon {
      width: 20px;
      margin-left: auto;
      display: ${({ hideProvider }) => hideProvider ? 'none' : null};
      
      div, svg {
        display: block;
        height: 20px;
        width: 20px;
        fill: ${({ theme }) => theme.primaryColor};
      }
    }
    
    @media ${({ theme }) => theme.mobile} {
      flex: 50% 1 0;    
      align-items: ${({ hideProvider }) => hideProvider ? 'center' : 'flex-start'};
      margin: ${({ hideProvider }) => hideProvider && '8px 16px 24px'};
      .provider {
          flex-grow : 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          
          > span {
            display: none;
          }
      }
      
      .provider-logo {
           @supports (object-fit: contain) {
                width: 100%;
                max-width: 126px;
           }
          height: 56px;
          margin-bottom: 16px;
      }
  
      .reduction-icon {
        width: 28px;
        margin: ${({ hideProvider }) => hideProvider ? '0 0 0 auto' : '16px 16px 0 24px'};
        
        svg {
          display: block;
          height: 28px;
          width: 28px;
          fill: ${({ theme }) => theme.primaryColor};
        }
      }
    }
`;

const DetailBlock = styled.div`
  > span {
      display: block;
      text-align: left;
      
      &:first-of-type {
        font-weight: 800;
        font-size: ${({ primary }) => primary ? '30px' : '18px'};
        margin-bottom: 8px;
      }
  }
 
  
  @media ${({ theme }) => theme.mobile} {
      flex-basis: 100%;
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      justify-content: space-between;
      
      box-sizing: border-box; 
      border-radius: 10px;
      
      min-height: ${({ primary }) => primary ? '48px' : '32px'};
      padding: ${({ primary }) => primary ? '16px' : '8px 16px'};
      background-color: ${({ primary, theme }) => primary ? theme.secondaryColor : null};
      font-weight: ${({ primary }) => primary ? '800' : '600'};
      
      > span:first-of-type {
        font-size: 16px;
        margin: 0;
      }
  }
`;

const ResultRow = ({ data, isActive, setActive, hideProvider, hideRadioCheck }) => {
  const { onMobile } = useMedia();
  return (
    <RowContainer
      as={!hideProvider ? "button" : null}
      active={isActive}
      onClick={setActive}
    >
      <ProductLabel hideProvider={hideProvider}>
        {hideProvider ? (
          <span css={{ fontSize: 14 }}>
            Product {data.has_automatic_risk_class_reduction ? 'met' : 'zonder'} looptijdrente
            <Info title="Looptijdrente">
              {looptijdExplanation}
            </Info>
          </span>
        ) : (
          <>
            {!hideRadioCheck && <RadioCheck active={isActive} />}
            <div className="provider">
              <img
                className="provider-logo"
                src={`https://h.hstatic.nl/bank/${data.provider_id}/logo-groot`}
                alt={data.provider_name}
              />
              <span>{data.label_name}</span>
            </div>
          </>
        )}
        <div className="reduction-icon">
          {(data.has_automatic_risk_class_reduction && !hideProvider) && <RiskReductionSvg/>}
        </div>
      </ProductLabel>
      {!hideProvider && <span className="provider-label">{data.label_name}</span>}
      <DetailBlock primary>
        <Currency value={data.average_monthly_payment} fontWeight='800' decimals={0} />
        <span>Gemiddelde maandlasten</span>
      </DetailBlock>
      <DetailBlock>
        <Percentage value={data.initial_interest_rate} fontWeight={!onMobile ? '800' : null} alwaysShowDecimals />
        <span>Aanvangsrente</span>
      </DetailBlock>
      <DetailBlock>
        <Percentage value={data.average_interest_rate} fontWeight={!onMobile ? '800' : null} alwaysShowDecimals />
        <span>Looptijdrente</span>
      </DetailBlock>
    </RowContainer>
  );
}

ResultRow.propTypes = {
  data: shapeResult.isRequired,
  isActive: bool,
  setActive: func,
  hideProvider: bool.isRequired,
  hideRadioCheck: bool,
}

ResultRow.defaultProps = {
  isActive: false,
  setActive: () => {},
  hideRadioCheck: false,
}

export default ResultRow;
